/**
 * The scoring engine, again, in TypeScript.
 *
 * <p><b>This is a second implementation of something that must not have two.</b> It exists
 * because the auditor scores offline, in a stockroom, and has to be shown a running total
 * there — and because the number they show the store manager and the number the server
 * computes on submission have to be the same number, arrived at the same way.</p>
 *
 * <p>Keeping them in step is not left to care. The C# tests generate a file of scenarios
 * and expected results, this engine is run against the same file, and CI fails if the file
 * is out of date or the two disagree. A comment saying "keep these in sync" would have been
 * obeyed for about a month.</p>
 *
 * <p><b>The one place it cannot be identical is arithmetic.</b> C# computes in decimal and
 * JavaScript has only float64. The weights involved are small and the error is around
 * 1e-12, which never survives rounding to two places — but "never" here means "not in any
 * case we have tried", which is exactly what the shared vectors are for.</p>
 */
import type { AnswerType, CriticalScope, NotApplicableScope } from '../api/types';

export type ExclusionReason =
  | 'None'
  | 'MarkedNotApplicable'
  | 'StructurallyNotApplicable'
  | 'Hidden'
  | 'NotScored';

export interface ScoredQuestion {
  id: string;
  weight: number;
  type: AnswerType;
  critical: CriticalScope;
  notApplicable: { allowed: boolean; scope: NotApplicableScope };
  scale: { min: number; max: number } | null;
  options: { id: string; score: number }[] | null;
  condition: { questionId: string; answer: boolean } | null;
}

export interface ScoredCategory {
  id: string;
  name: string;
  weight: number;
  questions: ScoredQuestion[];
}

export interface ScorableForm {
  scored: boolean;
  categories: ScoredCategory[];
}

export interface Answer {
  questionId: string;
  yesNo?: boolean | null;
  scaleValue?: number | null;
  selectedOptionIds?: string[];
  text?: string | null;
  markedNotApplicable?: boolean;
  structurallyNotApplicable?: boolean;
}

export interface QuestionScore {
  questionId: string;
  weight: number;
  exclusion: ExclusionReason;
  ratio: number | null;
  failedCritical: boolean;
  counted: boolean;
}

export interface CategoryScore {
  categoryId: string;
  name: string;
  declaredWeight: number;

  /**
   * What this category is worth on this visit: the declared weight after form-scope
   * exclusions shrank it, and nothing else. A category nobody has opened yet is still
   * worth every point the form gives it — which `effectiveWeight` deliberately is not.
   */
  weightInForm: number;
  effectiveWeight: number;
  score: number | null;
  zeroedByCritical: boolean;
  questions: QuestionScore[];
}

export interface ScoringResult {
  isScored: boolean;
  isComplete: boolean;
  score: number | null;

  /**
   * What the answers given so far come to, out of 100. **Not the result** — the result is
   * `score`, and it does not exist until the audit is finished.
   *
   * This is the number on the auditor's screen while they work, and it is a separate field
   * rather than a laxer `score` because the one of record has to keep refusing to exist on
   * an incomplete audit: scoring blanks as zero punishes unfinished work and skipping them
   * rewards it. A running figure may mean something weaker precisely because it is named
   * differently, and the screen says so beside it.
   */
  runningScore: number | null;
  zeroedByCritical: boolean;
  categories: CategoryScore[];
  unansweredQuestionIds: string[];
  nothingToScore: boolean;
}

const isAnswered = (a: Answer | undefined): boolean =>
  a != null &&
  (a.yesNo != null ||
    a.scaleValue != null ||
    (a.selectedOptionIds?.length ?? 0) > 0 ||
    (a.text ?? '').trim() !== '');

/** Free text cannot be graded, so it never scores and never carries weight. */
const scores = (type: AnswerType): boolean => type !== 'Text';

/**
 * The fraction of a question's weight an answer earns, 0 to 1.
 *
 * <p>A single choice scores its option out of a hundred: the others were alternatives to
 * it. A multiple choice scores the share of the whole list that was ticked: each option is
 * its own small question and they add up. The two denominators differ because the questions
 * differ — see FormQuestionOption on the server for the same note.</p>
 */
export function earnedRatio(question: ScoredQuestion, answer: Answer): number {
  const chosen = answer.selectedOptionIds ?? [];

  switch (question.type) {
    case 'YesNo':
      return answer.yesNo === true ? 1 : 0;

    case 'Scale': {
      const min = question.scale?.min ?? 0;
      const max = question.scale?.max ?? 0;
      const span = max - min;
      if (span <= 0) return 0;

      const value = Math.min(Math.max(answer.scaleValue ?? min, min), max);
      return (value - min) / span;
    }

    case 'SingleChoice': {
      const picked = (question.options ?? []).find((o) => chosen.includes(o.id));
      return picked ? picked.score / 100 : 0;
    }

    case 'MultipleChoice': {
      const options = question.options ?? [];
      const total = options.reduce((sum, o) => sum + o.score, 0);
      if (total <= 0) return 0;

      const earned = options.filter((o) => chosen.includes(o.id)).reduce((sum, o) => sum + o.score, 0);
      return Math.min(Math.max(earned / total, 0), 1);
    }

    default:
      return 0;
  }
}

/**
 * Whether the answer counts against the store, which is what makes a comment or a
 * photograph owed. A scale is negative below its midpoint; a choice is negative at anything
 * short of full marks.
 */
export function isNegative(question: ScoredQuestion, answer: Answer): boolean {
  switch (question.type) {
    case 'YesNo':
      return answer.yesNo === false;

    case 'Scale': {
      const min = question.scale?.min ?? 0;
      const max = question.scale?.max ?? 0;
      return answer.scaleValue != null && answer.scaleValue < (min + max) / 2;
    }

    case 'SingleChoice':
    case 'MultipleChoice':
      return (answer.selectedOptionIds?.length ?? 0) > 0 && earnedRatio(question, answer) < 1;

    default:
      return false;
  }
}

/**
 * Two decimal places, halves away from zero — what Math.Round(v, 2, AwayFromZero) does.
 *
 * <p>The nudge is the interesting part. A value that lands one float step below a .5
 * boundary got there by arithmetic rather than by intent, and 1e-9 is far above the error
 * these sums accumulate and far below anything a score distinguishes.</p>
 */
export function roundToTwo(value: number): number {
  const scaled = value * 100;
  const sign = scaled < 0 ? -1 : 1;
  const nudged = Math.abs(scaled) + 1e-9;

  return (sign * Math.trunc(nudged + 0.5)) / 100;
}

/** Which questions were shown. One forward pass, because conditions point backwards. */
function visibility(form: ScorableForm, answers: Map<string, Answer>): Map<string, boolean> {
  const shown = new Map<string, boolean>();

  for (const category of form.categories) {
    for (const question of category.questions) {
      if (!question.condition) {
        shown.set(question.id, true);
        continue;
      }

      const source = answers.get(question.condition.questionId);
      shown.set(
        question.id,
        (shown.get(question.condition.questionId) ?? true) &&
          source?.yesNo != null &&
          source.yesNo === question.condition.answer,
      );
    }
  }

  return shown;
}

function exclusionFor(
  question: ScoredQuestion,
  answer: Answer | undefined,
  shown: Map<string, boolean>,
): ExclusionReason {
  if (shown.get(question.id) === false) return 'Hidden';
  if (!answer) return 'None';

  // Structural exemption is reported ahead of the auditor's own judgement: when both are
  // set, the store was exempt anyway and that is the more useful fact.
  if (answer.structurallyNotApplicable) return 'StructurallyNotApplicable';

  if (answer.markedNotApplicable && question.notApplicable.allowed && question.critical === 'None') {
    return 'MarkedNotApplicable';
  }

  return 'None';
}

export function score(form: ScorableForm, answers: Answer[]): ScoringResult {
  // Last answer wins for a repeated question, as on the server.
  const byQuestion = new Map<string, Answer>();
  for (const answer of answers) byQuestion.set(answer.questionId, answer);

  const shown = visibility(form, byQuestion);
  const unanswered: string[] = [];
  const categories: CategoryScore[] = [];
  let formCriticalFailed = false;

  for (const category of form.categories) {
    const questionScores: QuestionScore[] = [];
    let appliedWeight = 0;
    let categoryScopeOut = 0;
    let formScopeOut = 0;
    let earned = 0;
    let categoryCriticalFailed = false;

    for (const question of category.questions) {
      const answer = byQuestion.get(question.id);

      // Free text leaves before any weight is counted. It carries none, so there is nothing
      // to redistribute — unlike a question that was excluded.
      if (!scores(question.type)) {
        if (!isAnswered(answer)) unanswered.push(question.id);

        questionScores.push({
          questionId: question.id,
          weight: 0,
          exclusion: 'NotScored',
          ratio: null,
          failedCritical: false,
          counted: false,
        });
        continue;
      }

      const exclusion = exclusionFor(question, answer, shown);

      if (exclusion !== 'None') {
        if (question.notApplicable.scope === 'Form') formScopeOut += question.weight;
        else categoryScopeOut += question.weight;

        questionScores.push({
          questionId: question.id,
          weight: question.weight,
          exclusion,
          ratio: null,
          failedCritical: false,
          counted: false,
        });
        continue;
      }

      if (!isAnswered(answer)) {
        unanswered.push(question.id);
        questionScores.push({
          questionId: question.id,
          weight: question.weight,
          exclusion: 'None',
          ratio: null,
          failedCritical: false,
          counted: true,
        });
        continue;
      }

      const ratio = earnedRatio(question, answer!);
      const failedCritical = question.critical !== 'None' && isNegative(question, answer!);

      if (failedCritical) {
        if (question.critical === 'Form') formCriticalFailed = true;
        else categoryCriticalFailed = true;
      }

      appliedWeight += question.weight;
      earned += question.weight * ratio;

      questionScores.push({
        questionId: question.id,
        weight: question.weight,
        exclusion: 'None',
        ratio,
        failedCritical,
        counted: true,
      });
    }

    // The category's whole scored mass, as the form declares it — not just the part that
    // has been answered. Taking it from appliedWeight instead made the figure move while
    // the audit was still being filled in, which matters here more than on the server: this
    // is the engine behind the number on the auditor's screen, and a running total that
    // rises when a full-marks answer is entered is a running total nobody trusts. On a
    // finished audit the two are the same, so no recorded score moves.
    const totalWeight = category.questions
      .filter((q) => q.type !== 'Text')
      .reduce((sum, q) => sum + q.weight, 0);

    // Form-scope exclusions shrink the category proportionally; category-scope ones do not.
    // That is the whole distinction between the two.
    const inForm = totalWeight > 0 ? (category.weight * (totalWeight - formScopeOut)) / totalWeight : 0;

    // Nothing counted here, so the category cannot be scored and must not pull the form's
    // average toward zero. It drops out of the sum — but only out of the sum. What it is
    // worth on this visit is inForm, and a category nobody has opened is still worth every
    // point the form gives it.
    const effective = appliedWeight > 0 ? inForm : 0;

    const categoryScore =
      appliedWeight > 0 ? (categoryCriticalFailed ? 0 : (earned / appliedWeight) * 100) : null;

    categories.push({
      categoryId: category.id,
      name: category.name,
      declaredWeight: category.weight,
      weightInForm: inForm,
      effectiveWeight: effective,
      score: categoryScore,
      zeroedByCritical: categoryCriticalFailed,
      questions: questionScores,
    });
  }

  if (!form.scored) {
    return {
      isScored: false,
      isComplete: unanswered.length === 0,
      score: null,
      runningScore: null,
      zeroedByCritical: false,
      categories,
      unansweredQuestionIds: unanswered,
      nothingToScore: false,
    };
  }

  // Computed once and used twice: the running figure and the result of record are the same
  // arithmetic over the same categories, so the number the auditor watched all morning is
  // the number they end on rather than one that jumps at the last answer.
  const weightInPlay = categories.reduce((sum, c) => sum + c.effectiveWeight, 0);

  let total =
    weightInPlay > 0
      ? categories.reduce((sum, c) => sum + c.effectiveWeight * (c.score ?? 0), 0) / weightInPlay
      : null;

  if (formCriticalFailed && total !== null) total = 0;

  const rounded = total === null ? null : roundToTwo(total);

  if (unanswered.length > 0) {
    return {
      isScored: true,
      isComplete: false,

      // score stays null. An unfinished audit has no result, and handing one out under the
      // same name as the real thing is how a half-filled form ends up quoted in a meeting.
      score: null,
      runningScore: rounded,
      zeroedByCritical: false,
      categories,
      unansweredQuestionIds: unanswered,
      nothingToScore: false,
    };
  }

  return {
    isScored: true,
    isComplete: true,
    score: rounded,
    runningScore: rounded,
    zeroedByCritical: formCriticalFailed,
    categories,
    unansweredQuestionIds: [],
    nothingToScore: total === null,
  };
}
